import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Link as MuiLink,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddShoppingCartOutlinedIcon from "@mui/icons-material/AddShoppingCartOutlined";

import { ShoppingCartService } from "../../services/shoppingCartService";

export const ShopItemsList = (props) => {
  const shoppingCartService = ShoppingCartService();
  const [quantities, setQuantities] = useState({});
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [error, setError] = useState(null);
  const [busyRow, setBusyRow] = useState(null);

  const rows = props.products?.result || [];

  const handleQtyChange = (no, value) => {
    setQuantities((prev) => ({ ...prev, [no]: value }));
  };

  const onBuy = async (item) => {
    const qty = parseInt(quantities[item.no] || 1, 10);
    if (!qty || qty < 1) return;
    setBusyRow(item.no);
    try {
      await shoppingCartService.addItem(item.no, qty);
      const cart = await shoppingCartService.getShoppingCart();
      if (cart && cart.items && props.updateCartItemCount) {
        props.updateCartItemCount();
      }
      setShowSuccessDialog(true);
    } catch (err) {
      setError(err);
      setShowErrorDialog(true);
    } finally {
      setBusyRow(null);
    }
  };

  return (
    <>
      <Card sx={{ overflow: "hidden", border: (t) => `1px solid ${t.palette.divider}`, boxShadow: "none" }}>
        <TableContainer>
          <Table size="medium">
            <TableHead>
              <TableRow sx={{ bgcolor: "background.default" }}>
                <TableCell sx={{ fontWeight: 700, textTransform: "uppercase", fontSize: 12, letterSpacing: 0.5 }}>
                  Description
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, textTransform: "uppercase", fontSize: 12, letterSpacing: 0.5 }}>
                  Stock
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, textTransform: "uppercase", fontSize: 12, letterSpacing: 0.5 }}>
                  Price
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, textTransform: "uppercase", fontSize: 12, letterSpacing: 0.5, width: 200 }}>
                  Quantity
                </TableCell>
                <TableCell align="right" sx={{ width: 64 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const stock = row.availableInventory ?? 0;
                const outOfStock = stock <= 0;
                const qty = quantities[row.no] ?? 1;
                return (
                  <TableRow
                    key={row.no}
                    hover
                    sx={{
                      "&:last-of-type td": { borderBottom: 0 },
                    }}
                  >
                    <TableCell>
                      <MuiLink
                        component={RouterLink}
                        to={`/item/${encodeURIComponent(row.no)}`}
                        underline="hover"
                        sx={{
                          color: "text.primary",
                          fontWeight: 500,
                          "&:hover": { color: "primary.main" },
                        }}
                      >
                        {row.description}
                      </MuiLink>
                      {row.brand && (
                        <Typography
                          variant="caption"
                          sx={{ display: "block", color: "text.secondary" }}
                        >
                          {row.brand}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: outOfStock ? "error.main" : "text.primary",
                        }}
                      >
                        {stock > 20 ? ">20" : stock}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        € {row.unitPrice}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        type="number"
                        size="small"
                        value={qty}
                        onChange={(e) => handleQtyChange(row.no, e.target.value)}
                        inputProps={{
                          min: 1,
                          max: stock,
                          style: { textAlign: "center", fontWeight: 600 },
                        }}
                        disabled={outOfStock}
                        sx={{ width: 90 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={() => onBuy(row)}
                        disabled={outOfStock || busyRow === row.no}
                        sx={{
                          color: "text.primary",
                          border: (t) => `1px solid ${t.palette.divider}`,
                          borderRadius: 1.5,
                          "&:hover": {
                            bgcolor: "primary.main",
                            color: "primary.contrastText",
                            borderColor: "primary.main",
                          },
                        }}
                        aria-label="Add to basket"
                      >
                        <AddShoppingCartOutlinedIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6, color: "text.secondary" }}>
                    No products to show
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Dialog open={showSuccessDialog} onClose={() => setShowSuccessDialog(false)}>
        <DialogTitle>Added to basket</DialogTitle>
        <DialogContent>
          <DialogContentText>The item has been added to your basket.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSuccessDialog(false)} autoFocus>OK</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showErrorDialog} onClose={() => setShowErrorDialog(false)}>
        <DialogTitle>Could not add to basket</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {error?.message || "An unexpected error occurred. Please try again."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowErrorDialog(false)} autoFocus>OK</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
